import { useCallback, useEffect, useRef, useState } from 'react'
import {
  OffsetPaginatedResponse,
  PrismaOffsetPagination,
} from '@/utils/fetchHelpers'
import { useAsync } from '@/utils/sharedHooks'

interface UseOffsetPaginatedQueryProps<
  TResponse extends OffsetPaginatedResponse<{ id: number }>,
> {
  fetchData: (pagination: PrismaOffsetPagination) => Promise<TResponse>
  take?: number
}

export const useOffsetPaginatedQuery = <
  TResponse extends OffsetPaginatedResponse<{ id: number }>,
>({
  fetchData,
  take = 20,
}: UseOffsetPaginatedQueryProps<TResponse>) => {
  const [activePage, setActivePage] = useState(1)
  const [response, setResponse] = useState<TResponse | null>(null)

  const requestIdRef = useRef(0)
  const prevFetchDataRef = useRef(fetchData)

  useEffect(() => {
    if (prevFetchDataRef.current !== fetchData) {
      prevFetchDataRef.current = fetchData
      setActivePage(1)
    }
  }, [fetchData])

  const skip = (activePage - 1) * take

  const { loading, error } = useAsync(
    useCallback(async () => {
      const requestId = ++requestIdRef.current

      try {
        const result = await fetchData({ skip, take })
        if (requestId === requestIdRef.current) {
          setResponse(result)
        }
        return result
      } catch (err: unknown) {
        // Ignore stale failures so an older request can't replace the
        // latest successful state or surface an outdated error.
        if (requestId !== requestIdRef.current) {
          return null
        }

        throw err
      }
    }, [fetchData, skip, take])
  )

  const handlePageChange = useCallback(
    (_: unknown, { activePage: page }: { activePage?: string | number }) => {
      if (!page) {
        return
      }

      setActivePage(Number(page))
    },
    []
  )

  const resetPage = useCallback(() => {
    setActivePage(1)
  }, [])

  const updateItem = useCallback(
    (item: Partial<TResponse['data'][number]> & { id: number }) => {
      setResponse((prev) => {
        if (!prev) {
          return prev
        }

        return {
          ...prev,
          data: prev.data.map((existing) =>
            existing.id === item.id ? { ...existing, ...item } : existing
          ),
        }
      })
    },
    []
  )

  const totalPages = response?.pagination?.totalItems
    ? Math.ceil(response.pagination.totalItems / Math.abs(take))
    : 0

  return {
    response,
    loading,
    error,
    activePage,
    totalPages,
    handlePageChange,
    resetPage,
    updateItem,
  }
}
